import React, { useEffect, useState } from 'react';
import {
  VStack, Text, Box, HStack, Flex, Button,
} from '@chakra-ui/react';
import { IconType } from 'react-icons';
import deportesApi, {
  Disciplina, Categoria, Rama, CategoriaInventario,
} from '../../api/deportesApi';

export const ROJO = '#5b182b';
export const ROJO_HOVER = '#691f30';
export { API_UPLOADS } from '../../api/deportesApi';

// Envoltorio de página: solo contenedor con padding (el nombre del módulo ya
// lo muestra el Navbar del layout, por lo que no se pinta banner de título).
// Las props icon/titulo/subtitulo se mantienen por compatibilidad con las
// páginas que las siguen pasando, pero se ignoran.
export const PaginaDeportes: React.FC<{
  icon?: IconType;
  titulo?: string;
  subtitulo?: string;
  children: React.ReactNode;
}> = ({ children }) => (
  <VStack spacing={6} align="stretch" px={{ base: 2, md: 4 }} pt={79} pb={4}>
    {children}
  </VStack>
);

// ─────────── Título de sección ───────────
export const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box borderBottom={`2px solid ${ROJO}`} pb={1} mb={1} mt={3}>
    <Text fontSize="xs" fontWeight="bold" color={ROJO} textTransform="uppercase" letterSpacing="wide">
      {children}
    </Text>
  </Box>
);

// ─────────── Campo de solo lectura ───────────
export const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" fontWeight="bold">{label}</Text>
    <Text fontSize="sm">{value || '—'}</Text>
  </Box>
);

// ─────────── Paginación reutilizable ───────────
interface PaginacionProps {
  currentPage: number;
  totalPages: number;
  total: number;
  indexPrimero: number;
  indexUltimo: number;
  onChange: (p: number) => void;
}
export const Paginacion: React.FC<PaginacionProps> = ({
  currentPage, totalPages, total, indexPrimero, indexUltimo, onChange,
}) => {
  if (totalPages <= 1) return null;
  return (
    <Flex mt={4} justify="space-between" align="center" flexWrap="wrap" gap={2}>
      <Text fontSize="sm" color="gray.500">
        Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, total)} de {total}
      </Text>
      <HStack spacing={1}>
        <Button size="xs" onClick={() => onChange(1)} isDisabled={currentPage === 1}>«</Button>
        <Button size="xs" onClick={() => onChange(Math.max(1, currentPage - 1))} isDisabled={currentPage === 1}>‹</Button>
        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
          const page = Math.max(1, currentPage - 2) + i;
          if (page > totalPages) return null;
          return (
            <Button
              key={page}
              size="xs"
              bg={currentPage === page ? ROJO : undefined}
              color={currentPage === page ? 'white' : undefined}
              _hover={currentPage === page ? { bg: ROJO_HOVER } : undefined}
              onClick={() => onChange(page)}
            >
              {page}
            </Button>
          );
        })}
        <Button size="xs" onClick={() => onChange(Math.min(totalPages, currentPage + 1))} isDisabled={currentPage === totalPages}>›</Button>
        <Button size="xs" onClick={() => onChange(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
      </HStack>
    </Flex>
  );
};

// ─────────── Formato de fecha ───────────
export const fmtFecha = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

export const fmtMoneda = (v?: number | string | null) =>
  v !== null && v !== undefined && v !== ''
    ? `$${Number(v).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`
    : '—';

// ─────────── Hook de catálogos + localidades ───────────
export interface Catalogos {
  disciplinas: Disciplina[];
  categorias: Categoria[];
  ramas: Rama[];
  categoriasInventario: CategoriaInventario[];
  localidades: any[];
  cargando: boolean;
}

export const useCatalogos = (): Catalogos => {
  const [disciplinas, setDisciplinas] = useState<Disciplina[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [ramas, setRamas] = useState<Rama[]>([]);
  const [categoriasInventario, setCategoriasInventario] = useState<CategoriaInventario[]>([]);
  const [localidades, setLocalidades] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let activo = true;
    (async () => {
      const [d, c, r, ci, l] = await Promise.all([
        deportesApi.listarDisciplinas().catch(() => []),
        deportesApi.listarCategorias().catch(() => []),
        deportesApi.listarRamas().catch(() => []),
        deportesApi.listarCategoriasInventario().catch(() => []),
        deportesApi.listarLocalidades().catch(() => []),
      ]);
      if (!activo) return;
      setDisciplinas(d || []);
      setCategorias(c || []);
      setRamas(r || []);
      setCategoriasInventario(ci || []);
      setLocalidades(l || []);
      setCargando(false);
    })();
    return () => { activo = false; };
  }, []);

  return { disciplinas, categorias, ramas, categoriasInventario, localidades, cargando };
};

// Calcula edad a partir de fecha de nacimiento (yyyy-mm-dd)
export const calcularEdad = (fechaNacimiento?: string | null): number | null => {
  if (!fechaNacimiento) return null;
  const fn = new Date(fechaNacimiento);
  if (isNaN(fn.getTime())) return null;
  const hoy = new Date();
  let edad = hoy.getFullYear() - fn.getFullYear();
  const m = hoy.getMonth() - fn.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < fn.getDate())) edad--;
  return edad >= 0 ? edad : null;
};

export const nombreCompleto = (d: { nombre?: string; apPaterno?: string | null; apMaterno?: string | null }) =>
  [d.nombre, d.apPaterno, d.apMaterno].filter(Boolean).join(' ');
